/* ─── Root ─── */

.root {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.root[data-disabled] {
  opacity: 0.38;
  cursor: not-allowed;
}

/* ─── Label ─── */

.label {
  font: var(--type-label-lg);
  letter-spacing: var(--tracking-label-lg);
  font-family: var(--font-sans);
  color: var(--md-on-surface);
}

.root[data-invalid] .label {
  color: var(--md-error);
}

/* ─── Input group (outlined border) ─── */

.inputGroup {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-1);
  min-height: 3rem;
  padding: var(--space-1) var(--space-3);
  border: 1px solid var(--md-outline);
  border-radius: var(--shape-xs);
  background: transparent;
  transition: border-color var(--motion-duration-short3)
      var(--motion-easing-standard),
    box-shadow var(--motion-duration-short3) var(--motion-easing-standard);
}

.inputGroup:focus-within {
  border-color: var(--md-primary);
  box-shadow: 0 0 0 1px var(--md-primary);
}

.root[data-invalid] .inputGroup {
  border-color: var(--md-error);
}

.root[data-invalid] .inputGroup:focus-within {
  border-color: var(--md-error);
  box-shadow: 0 0 0 1px var(--md-error);
}

.leading {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  color: var(--md-on-surface-variant);
}

/* ─── Chips ─── */

.chips {
  display: contents;
}

.chip {
  font: var(--type-label-lg);
  letter-spacing: var(--tracking-label-lg);
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  height: 2rem;
  padding-inline: var(--space-2);
  font-family: var(--font-sans);
  color: var(--md-on-surface);
  background: var(--md-surface-container-high);
  border-radius: var(--shape-sm);
  white-space: nowrap;
}

.chipRemove {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  border: none;
  border-radius: var(--shape-full);
  background: transparent;
  color: var(--md-on-surface-variant);
  cursor: pointer;
  flex-shrink: 0;
}

.chipRemove::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: currentColor;
  opacity: 0;
  transition: opacity var(--motion-duration-short3);
  pointer-events: none;
}

@media (hover: hover) {
  .chipRemove:hover::before {
    opacity: var(--state-hover);
  }
}

.chipRemove:focus-visible::before {
  opacity: var(--state-focus);
}

.chipRemove:active::before {
  opacity: var(--state-pressed);
}

.chipRemove:focus-visible {
  --focus-ring-offset: var(--focus-ring-offset-outset);
}

.chipRemove svg {
  width: 0.625rem;
  height: 0.625rem;
}

/* ─── Input ─── */

.input {
  font: var(--type-body-lg);
  letter-spacing: var(--tracking-body-lg);
  all: unset;
  flex: 1;
  min-width: 5rem;
  font-family: var(--font-sans);
  color: var(--md-on-surface);
}

.input::placeholder {
  color: var(--md-on-surface-variant);
}

.root[data-disabled] .input {
  cursor: not-allowed;
}

/* ─── Spinner ─── */

.spinner {
  flex-shrink: 0;
}

/* ─── Clear button ─── */

.clear {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* 24px: the minimum target size at AA (WCAG 2.5.8), and as large as this can
     get without crowding the trigger beside it. */
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  border: none;
  border-radius: var(--shape-full);
  background: transparent;
  color: var(--md-on-surface-variant);
  cursor: pointer;
  flex-shrink: 0;
}

.clear::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: currentColor;
  opacity: 0;
  transition: opacity var(--motion-duration-short3);
  pointer-events: none;
}

@media (hover: hover) {
  .clear:hover::before {
    opacity: var(--state-hover);
  }
}

.clear:focus-visible::before {
  opacity: var(--state-focus);
}

.clear:active::before {
  opacity: var(--state-pressed);
}

.clear:focus-visible {
  --focus-ring-offset: var(--focus-ring-offset-outset);
}

.clear svg {
  width: 0.875rem;
  height: 0.875rem;
}

/* ─── Trigger (chevron button) ─── */

.trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--md-on-surface-variant);
  cursor: pointer;
  flex-shrink: 0;
}

/* ─── Chevron ─── */

.chevron {
  width: 1rem;
  height: 1rem;
  transition: transform var(--motion-duration-short3)
    var(--motion-easing-standard);
}

.trigger[data-popup-open] .chevron {
  transform: rotate(180deg);
}

/* ─── Positioner ─── */

.positioner {
  z-index: var(--z-popover);
}

/* ─── Popup ─── */

.popup {
  background: var(--md-surface-container);
  border-radius: var(--shape-sm);
  box-shadow: var(--elevation-2);
  min-width: var(--anchor-width);
  max-height: 20rem;
  overflow: auto;
  outline: none;
  padding-block: var(--space-1);
}

.popup[data-starting-style],
.popup[data-ending-style] {
  opacity: 0;
  transform: scale(0.95);
}

.popup {
  transition: opacity var(--motion-duration-short3)
      var(--motion-easing-standard),
    transform var(--motion-duration-short3) var(--motion-easing-standard);
}

/* ─── List ─── */

.list {
  outline: none;
}

/* ─── Item ─── */

.item {
  font: var(--type-body-lg);
  letter-spacing: var(--tracking-body-lg);
  display: flex;
  align-items: center;
  gap: var(--space-2);
  min-height: 3rem;
  padding-block: var(--space-2);
  padding-inline: var(--space-3);
  font-family: var(--font-sans);
  color: var(--md-on-surface);
  cursor: pointer;
  outline: none;
  user-select: none;
  transition: background var(--motion-duration-short3)
    var(--motion-easing-standard);
}

.item[data-highlighted] {
  background: var(--md-surface-container-highest);
}

.item[data-disabled] {
  opacity: 0.38;
  cursor: not-allowed;
}

/* ─── Item indicator ─── */

.itemIndicator {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
  color: var(--md-primary);
}

.itemIndicator svg {
  width: 0.75rem;
  height: 0.75rem;
}

/* ─── Group ─── */

.group {
  padding-block: var(--space-1);
}

.group + .group {
  border-block-start: 1px solid var(--md-outline-variant);
}

/* ─── Group label ─── */

.groupLabel {
  font: var(--type-label-lg);
  letter-spacing: var(--tracking-label-lg);
  padding-inline: var(--space-3);
  padding-block: var(--space-2);
  font-family: var(--font-sans);
  font-weight: 600;
  color: var(--md-on-surface-variant);
}

/* ─── Empty state ─── */

.empty {
  font: var(--type-body-lg);
  letter-spacing: var(--tracking-body-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-4);
  font-family: var(--font-sans);
  color: var(--md-on-surface-variant);
}

/* ─── Description ─── */

.description {
  font: var(--type-body-lg);
  letter-spacing: var(--tracking-body-lg);
  font-family: var(--font-sans);
  color: var(--md-on-surface-variant);
  margin: 0;
}

/* ─── Error ─── */

.error {
  font: var(--type-body-lg);
  letter-spacing: var(--tracking-body-lg);
  font-family: var(--font-sans);
  color: var(--md-error);
  margin: 0;
}

/* ─── Status ─── */

.status {
  font: var(--type-body-md);
  letter-spacing: var(--tracking-body-md);
  color: var(--md-on-surface-variant);
  padding: var(--space-2) var(--space-4);
}

.status:empty {
  padding: 0;
}
